<template>
  <v-btn
    v-bind="attrs"
    :color="getColor"
    :elevation="getElevation"
    :variant="variant"
    :icon="iconBtn && !iconWithBackground"
    :class="computedClasses"
    @click="handleClick"
  >
    <slot>{{ label }}</slot>
  </v-btn>
</template>

<script lang="ts" setup>
import { useAttrs, computed } from 'vue'

const props = defineProps({
  variant: { type: String, default: 'elevated' },
  label: { type: String, default: '' },
  iconBtn: { type: Boolean, default: false },
  iconWithBackground: { type: Boolean, default: false },
  scroll: { type: Boolean, default: false },
  small: { type: Boolean, default: false },
})

const emit = defineEmits(['click', 'update:modelValue'])
const isSmall = computed(() => props.small)
const attrs = useAttrs()

const getColor = computed(() => {
  let color = attrs.color as string | undefined
  if (!color) color = 'blue'

  return color
})

const getElevation = computed(() => {
  let elevation = attrs.elevation as string | undefined
  if (!elevation) {
    elevation = props.iconBtn ? '0' : '1'
  }
  return parseInt(elevation)
})

const computedClasses = computed(() => {
  const attrClass = attrs.class

  let classList: string[] = []

  if (typeof attrClass === 'string') {
    classList = attrClass.trim().split(/\s+/)
  } else if (Array.isArray(attrClass)) {
    classList = attrClass.flatMap(c => typeof c === 'string' ? c.split(/\s+/) : [])
  } else if (typeof attrClass === 'object' && attrClass !== null) {
    classList = Object.keys(attrClass).filter(key => attrClass[key])
  }

  const color = attrs.color as string | undefined
  const textClass =
    color === 'secondary' ? 'text-primary' :
      color === 'grey-stroke' ? 'text-black-75' :
        ''

  const additional = [
    props.iconBtn ? 'btn-fab' : '',
    props.iconBtn && props.small ? 'btn-fab--small' : '',
    `elevation-${getElevation.value}`,
    textClass,
  ]

  const all = [...classList, ...additional].filter(Boolean)

  return [...new Set(all)]
})


function handleClick(event: MouseEvent) {
  if (props.scroll) {
    const ele = document.getElementById('main')
    if (ele) {
      window.scrollTo({ top: ele.offsetTop, behavior: 'smooth' })
    }
  }
  emit('click', event)
}
</script>
<style lang="scss" scoped>
.btn-fab,
.btn-fab--small,
.btn-fab--x-small {
  min-width: unset !important;
  padding: 0 !important;
}

.btn-fab {
  border-radius: 50%;
  width: map.get($fab-sizes, 'default') + px;
  height: map.get($fab-sizes, 'default') + px;
  padding: 0;
}

.btn-fab--small {
  width: map.get($fab-sizes, 'small') + px;
  height: map.get($fab-sizes, 'small') + px;
  min-width: map.get($fab-sizes, 'small') + px;
  min-height: map.get($fab-sizes, 'small') + px;
}

.btn-fab--x-small {
  width: map.get($fab-sizes, 'x-small') + px;
  height: map.get($fab-sizes, 'x-small') + px;
  min-width: map.get($fab-sizes, 'x-small') + px;
  min-height: map.get($fab-sizes, 'x-small') + px;

  .mdi {
    left: 0;
    width: 18px;
    height: 18px;
  }
}

.v-btn {
  .v-btn__overlay {
    background-color: rgba(var(--v-theme-primary), var(--v-theme-main-opacity)) !important;
  }
}

:global(.btn-no-hover-overlay:hover .v-btn__overlay) {
  background-color: transparent !important;
}
</style>
